import type Url from "@core-ui/ApiServices/Types/Url";
import useWatch from "@core-ui/hooks/useWatch";
import type { SearchFragmentInfo } from "@ext/serach/utils/ArticleFragmentCounter/ArticleFragmentCounter";
import { createLinkFocusItem, type FocusItem, FocusItemsCollector } from "@ext/serach/utils/FocusItemsCollector";
import type {
	LinkOpenSideEffectOptions,
	RowSearchResult,
	SearchItemRow,
	SearchItemRowId,
} from "@ext/serach/utils/SearchRowsModel";
import { useCallback, useMemo, useState } from "react";
import type { ArticleRow, ArticleRowExpanderItem, ArticleRowItem, ArticleRowParagraphItem, Row } from "../rowTypes";

const DEFAULT_SHOW_PARAGRAPH = 3;
const HIDDEN_EXPAND_COUNT = 5;

export interface SearchLinkTarget {
	/** Where to navigate — carries the highlight query. */
	url: Url;
	pathname: string;
	fragmentInfo?: SearchFragmentInfo;
}

export type OnLinkOpen = (target: SearchLinkTarget) => void;

export const searchLinkTarget = (item: { url: Url; openSideEffect: LinkOpenSideEffectOptions }): SearchLinkTarget => ({
	url: item.url,
	pathname: item.openSideEffect.params.pathname,
	fragmentInfo: item.openSideEffect.params.fragmentInfo,
});

export interface UseSearchResultsArgs {
	rows: RowSearchResult[];
	onLinkOpen: OnLinkOpen;
}

export interface UseSearchResultsResult {
	results: Row[];
	focus: SearchFocus;
}

export interface SearchFocus {
	current: FocusItem | undefined;
	/** True while the focus is being driven by the keyboard, so the view knows to scroll it into view. */
	byKeyboard: boolean;
	set: (id: SearchItemRowId | undefined) => void;
	move: (delta: number) => void;
	handleKeyDown: (e: { code: string }) => boolean;
}

interface FocusState {
	item: FocusItem | undefined;
	byKeyboard: boolean;
}

export const useSearchResults = (args: UseSearchResultsArgs): UseSearchResultsResult => {
	const { rows, onLinkOpen } = args;
	const [showingMap, setShowingMap] = useState(new Map<SearchItemRowId, number>());
	const [focus, setFocus] = useState<FocusState>({ item: undefined, byKeyboard: false });
	const focusItem = focus.item;

	const setFocusItem = useCallback((item: FocusItem | undefined) => {
		setFocus({ item, byKeyboard: false });
	}, []);

	const focusByKeyboard = useCallback((item: FocusItem | undefined) => {
		setFocus({ item, byKeyboard: true });
	}, []);

	useWatch(() => {
		setFocus({ item: undefined, byKeyboard: false });
		setShowingMap(new Map());
	}, [rows]);

	const setShowing = useCallback(
		(id: SearchItemRowId, newValue: number) => {
			const newMap = new Map(showingMap);
			newMap.set(id, newValue);
			setShowingMap(newMap);
		},
		[showingMap],
	);

	const { results, focusableCollector } = useMemo(() => {
		const results: Row[] = [];
		const focusableCollector = new FocusItemsCollector();

		for (const row of rows) {
			const type = row.type;
			switch (type) {
				case "catalog": {
					results.push(row);
					focusableCollector.addLinkItem(row, () => {
						onLinkOpen(searchLinkTarget(row));
					});
					break;
				}
				case "article": {
					focusableCollector.addLinkItem(row, () => {
						onLinkOpen(searchLinkTarget(row));
					});
					const items = getArticleRowItems(
						row.id,
						row.items,
						showingMap,
						focusableCollector,
						setShowing,
						setFocusItem,
						onLinkOpen,
					);

					const item: ArticleRow = { ...row, items };

					results.push(item);
					break;
				}
				default:
					throw new Error(`Unexpected search result row type ${type}`);
			}
		}

		return { results, focusableCollector };
	}, [rows, showingMap, onLinkOpen, setShowing, setFocusItem]);

	const setFocusId = useCallback(
		(id: SearchItemRowId | undefined) => {
			setFocusItem(id ? focusableCollector.get(id) : undefined);
		},
		[focusableCollector, setFocusItem],
	);

	const moveFocus = useCallback(
		(delta: number) => {
			if (!focusItem) return void focusByKeyboard(focusableCollector.first());

			if (focusItem.type === "temp") {
				focusByKeyboard(focusableCollector.getByIndex(focusItem.index + (delta > 0 ? delta - 1 : delta)));
				return;
			}

			focusByKeyboard(focusableCollector.get(focusItem.id, delta));
		},
		[focusItem, focusableCollector, focusByKeyboard],
	);

	const handleKeyDown = useCallback(
		(e: { code: string }) => {
			if (e.code === "ArrowUp") {
				moveFocus(-1);
				return true;
			}

			if (e.code === "ArrowDown") {
				moveFocus(1);
				return true;
			}

			if (e.code === "Enter" && focusItem) {
				focusItem.onClick();
				return true;
			}

			return false;
		},
		[moveFocus, focusItem],
	);

	return {
		results,
		focus: {
			current: focusItem,
			byKeyboard: focus.byKeyboard,
			set: setFocusId,
			move: moveFocus,
			handleKeyDown,
		},
	};
};

function getArticleRowItems(
	parentId: SearchItemRowId,
	items: SearchItemRow[],
	showingMap: Map<SearchItemRowId, number>,
	focusableCollector: FocusItemsCollector,
	setShowing: (id: SearchItemRowId, count: number) => void,
	setFocusItem: (item: FocusItem) => void,
	onLinkOpen: OnLinkOpen,
): ArticleRowItem[] {
	const addHiddenOrExpander = (
		res: ArticleRowItem[],
		paragraphCountBuffer: number,
		showing: number,
		expanderId: SearchItemRowId | undefined,
		hiddens: ArticleRowParagraphItem[],
	) => {
		if (hiddens.length === 1) {
			const firstHidden = hiddens[0];
			res.push(firstHidden);
			if (firstHidden.focusable)
				focusableCollector.addLinkItem(firstHidden, () => {
					onLinkOpen(searchLinkTarget(firstHidden));
				});

			return true;
		}

		if (!expanderId) return false;
		if (paragraphCountBuffer <= showing) return false;
		const hiddenCount = paragraphCountBuffer - showing;
		const expander: ArticleRowExpanderItem = {
			type: "expander",
			focusable: true,
			id: expanderId,
			count: hiddenCount,
			onClick: () => {
				const newShowing = showing + HIDDEN_EXPAND_COUNT;
				const lastShowingItem =
					hiddens.length === HIDDEN_EXPAND_COUNT + 1
						? hiddens[hiddens.length - 1]
						: hiddens[Math.min(HIDDEN_EXPAND_COUNT, hiddens.length) - 1];
				setShowing(expanderId, newShowing);
				setFocusItem(
					lastShowingItem.focusable
						? createLinkFocusItem(lastShowingItem, () => {
								onLinkOpen(searchLinkTarget(lastShowingItem));
							})
						: {
								type: "temp",
								id: lastShowingItem.id,
								index: focusableCollector.getIndex(expanderId),
								onClick: () => {},
							},
				);
			},
		};

		res.push(expander);
		focusableCollector.addItem(expander);
		return true;
	};

	const handleItemsRecursively = (parentId: SearchItemRowId, items: SearchItemRow[], singleBlockFocus: boolean) => {
		const res: ArticleRowItem[] = [];

		let paragraphCountBuffer = 0;
		let expanderId: SearchItemRowId | undefined;
		let showing = DEFAULT_SHOW_PARAGRAPH;
		let hiddens: ArticleRowParagraphItem[] = [];

		const resetParagraphGroup = () => {
			paragraphCountBuffer = 0;
			expanderId = undefined;
			showing = DEFAULT_SHOW_PARAGRAPH;
			hiddens = [];
		};

		const setParagraphGroupStart = (groupStartId: SearchItemRowId) => {
			expanderId = `${parentId}_expand_${groupStartId}`;
			showing = showingMap.get(expanderId) ?? DEFAULT_SHOW_PARAGRAPH;
		};

		items.forEach((item) => {
			const type = item.type;
			switch (type) {
				case "link": {
					const rowItem: ArticleRowParagraphItem = {
						...item,
						focusable: !singleBlockFocus,
					};
					if (paragraphCountBuffer === 0) setParagraphGroupStart(rowItem.id);
					paragraphCountBuffer++;

					if (paragraphCountBuffer > showing) {
						hiddens.push(rowItem);
						break;
					}

					if (!singleBlockFocus)
						focusableCollector.addLinkItem(item, () => {
							onLinkOpen(searchLinkTarget(item));
						});

					res.push(rowItem);
					break;
				}
				case "block":
				case "file-block": {
					addHiddenOrExpander(res, paragraphCountBuffer, showing, expanderId, hiddens);
					resetParagraphGroup();

					if (!singleBlockFocus)
						focusableCollector.addLinkItem(item, () => {
							onLinkOpen(searchLinkTarget(item));
						});

					res.push({
						...item,
						focusable: !singleBlockFocus,
						children: handleItemsRecursively(
							item.id,
							item.children,
							singleBlockFocus || item.type === "file-block",
						),
					});
					break;
				}
				case "diagram": {
					addHiddenOrExpander(res, paragraphCountBuffer, showing, expanderId, hiddens);
					resetParagraphGroup();

					if (!singleBlockFocus)
						focusableCollector.addLinkItem(item, () => {
							onLinkOpen(searchLinkTarget(item));
						});

					res.push({
						...item,
						focusable: !singleBlockFocus,
						children: handleItemsRecursively(item.id, item.children, true),
					});
					break;
				}
				default:
					throw new Error(`Unexpected search result row item type ${type}`);
			}
		});

		addHiddenOrExpander(res, paragraphCountBuffer, showing, expanderId, hiddens);
		return res;
	};

	const res = handleItemsRecursively(parentId, items, false);
	return res;
}
